import React from 'react';
import { Button, Col, Dropdown, Menu, Popconfirm, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import { RiArrowLeftLine, RiErrorWarningLine } from 'react-icons/ri';
import { DownOutlined } from '@ant-design/icons';
import { PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import urlPagePushNotificationEdit from '../../../../../../urls/urlPagePushNotificationEdit';
import urlPagePushNotifications from '../../../../../../urls/urlPagePushNotifications';
import { Permissions } from '../../../../../../const/permissions';
import handleResponse from '../../../../../../lib/handleResponse';
import mkPreparedTemplateData from '../../../../../Content/utils/mkPreparedTemplateData';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../../layout/components/action-button';
import NotificationDetailsCard from '../../../../../Content/components/NotificationDetailsCard';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../../../const/system';
import { TYPE_DICTIONARY } from '../../../../../Template builder/const/templates';
import { useGetDictionaryQuery } from '../../../../../Translations/api/dictionariesApiSlice';
import { useGetLanguagesQuery } from '../../../../../Translations/api/languagesApiSlice';
import {
  useDeletePushNotificationMutation,
  useGetSpecificPushNotificationQuery,
} from '../../../../api/pushNotificationsApiSlice';



const PagePushNotificationDetails = () => {
  const { notificationId } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: pushNotification = {}, isFetching } = useGetSpecificPushNotificationQuery({ notificationId, includes: '?include=dynamic_data,template' });

  const dictionaryField = pushNotification?.dynamic_data?.data?.find((item) => item.type === TYPE_DICTIONARY);

  const { data: dictionary = {} } = useGetDictionaryQuery({
    id: dictionaryField?.value,
    include: 'translations,structure,structure.fields.translations',
  }, { skip: !dictionaryField?.value });

  const { data: {
    data: languages = [],
  } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' }, { skip: !dictionaryField });

  const [ deletePushNotification, { isLoading: isPushNotificationDeleting } ] = useDeletePushNotificationMutation();

  const data = mkPreparedTemplateData({
    templateData: pushNotification?.template?.data?.fields?.data,
    dynamicData: pushNotification?.dynamic_data?.data,
    dictionary,
    languages,
  });

  const handleDelete = (id) => {
    deletePushNotification(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully', [ () => navigate(urlPagePushNotifications()) ]))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const actionItems = (
    <Menu>
      <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.EDIT ]}>
        <Menu.Item
          key="edit"
          icon={<PiPencilSimpleLine size={18} />}
          onClick={() => navigate(urlPagePushNotificationEdit({ notificationId: pushNotification.id }))}
        >
          <IntlMessages id="ui-general-edit" />
        </Menu.Item>
      </PrivateComponent>
      <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.DELETE ]}>
        <Popconfirm
          title={<IntlMessages id="ui-general-delete-confirm-message" />}
          placement="topLeft"
          onConfirm={() => handleDelete(pushNotification.id)}
          okText={<IntlMessages id="ui-general-yes" />}
          cancelText={<IntlMessages id="ui-general-no" />}
          icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
          okButtonProps={{ danger: true }}
        >
          <Menu.Item
            key="delete"
            icon={<PiTrashSimple size={18} />}
            onClick={() => {}}
          >
            <IntlMessages id="ui-general-delete" />
          </Menu.Item>
        </Popconfirm>
      </PrivateComponent>
    </Menu>
  );

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPagePushNotifications()}>
              <IntlMessages id='push-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={pushNotification?.id ?? EMPTY_VALUE_PLACEHOLDER}
        />

        <Col md={10} span={24} className='text-end'>
          <Space size='middle'>
            {!pushNotification?.has_schedules && (
              <PrivateComponent
                allowedPermissions={[
                  Permissions.OTHER.NOTIFICATIONS.PUSH.EDIT,
                  Permissions.OTHER.NOTIFICATIONS.PUSH.DELETE,
                ]}
              >
                <Dropdown overlay={actionItems}>
                  <Button onClick={(ev) => ev.preventDefault()}>
                    <Space size='middle'>
                      <IntlMessages id='ui-general-action' />
                      <DownOutlined />
                    </Space>
                  </Button>
                </Dropdown>
              </PrivateComponent>
            )}
            <ActionButton
              title={intl.formatMessage({ id: 'ui-general-back-to-list' })}
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() => navigate(urlPagePushNotifications())}
            />
          </Space>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <NotificationDetailsCard
            title={<IntlMessages id="ui-general-push-notification" />}
            data={pushNotification}
            isLoadingCard={isPushNotificationDeleting}
            isFetching={isFetching}
            dictionary={dictionary}
            languages={languages}
            templateCardsData={data}
          />
        </Col>
      </Row>
    </>
  );
};

export default PagePushNotificationDetails;
